import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { glyphPaths } from '../icons/icon-paths';
import '../utils/benefits-slider.css';
import { SimpleResponsivePicture } from './SimpleResponsiveImage';

// Register ScrollTrigger
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

// === CONFIGURABLE VARIABLES ===
const FOLDERS = {
  ETH: './img/quick_swap/eth/',
  AGE: './img/quick_swap/age/',
};
const IMAGE_COUNT = 6;
const ANIMATION_DURATION = 0.125; // seconds for each fade
const SCROLL_START = 'top 70%';
const SCROLL_END = 'bottom 30%';
const BORDER_RADIUS = 'var(--border-radius)';
// === END CONFIGURABLE VARIABLES ===

function getImagePaths(folder) {
  return Array.from({ length: IMAGE_COUNT }, (_, i) => `${folder}${String(i + 1).padStart(2, '0')}.png`);
}

// Generate a random 4-character code
function generateRandomCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

const BenefitsImageSlider = ({ sliderApproach = 5 }) => {
  // Default to ETH
  const [folderKey, setFolderKey] = useState('ETH');
  const IMAGES = getImagePaths(FOLDERS[folderKey]);
  const containerRef = useRef(null);
  const imagesRef = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showSlider, setShowSlider] = useState(false);
  // Generate random codes once per folder
  const [codes, setCodes] = useState(() => IMAGES.map(() => generateRandomCode()));

  // Update codes when folder changes
  useEffect(() => {
    setCodes(IMAGES.map(() => generateRandomCode()));
    setActiveIndex(0);
  }, [folderKey]);

  useEffect(() => {
    if (!containerRef.current) return;
    
    // Wait for images to be available
    const timer = setTimeout(() => {
      const images = imagesRef.current;
      
      // Check if images are available
      if (!images || !images[0]) {
        return;
      }
      
      // Add null checks before setting GSAP properties
      images.forEach(img => {
        if (img) {
          gsap.set(img, { opacity: 0, zIndex: 1 });
        }
      });
      
      if (images[0]) {
        gsap.set(images[0], { opacity: 1, zIndex: 2 });
      }

      // Direct ScrollTrigger control for more precise timing
      ScrollTrigger.create({
        trigger: containerRef.current,
        start: SCROLL_START,
        end: SCROLL_END,
        onUpdate: (self) => {
          const progress = self.progress;
          
          // Complete all transitions by 50% scroll progress
          const transitionProgress = Math.min(progress * 2, 1); // Double the progress to complete by 50%
          const imageIndex = Math.min(Math.floor(transitionProgress * IMAGES.length), IMAGES.length - 1);
          
          // Update active index
          setActiveIndex(imageIndex);
          
          // Update image visibility
          imagesRef.current.forEach((img, i) => {
            if (img) {
              gsap.set(img, {
                opacity: i <= imageIndex ? 1 : 0,
                zIndex: i + 2
              });
            }
          });
          
          // Show slider at the end of scroll
          if (progress === 1) setShowSlider(true);
          else setShowSlider(false);
        },
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      // Kill any remaining GSAP animations on the images
      if (imagesRef.current) {
        imagesRef.current.forEach(img => {
          if (img) {
            gsap.killTweensOf(img);
          }
        });
      }
    };
  }, [folderKey]);

  // Slider manual control
  const handleSliderChange = (e) => {
    const idx = parseInt(e.target.value, 10);
    setActiveIndex(idx);
    // All images with index <= idx should be visible
    imagesRef.current.forEach((img, i) => {
      if (img) {
        gsap.to(img, { opacity: i <= idx ? 1 : 0, zIndex: i + 2, duration: 0.4 });
      }
    });
  };

  // Ensure correct stacking on activeIndex change (for scroll or slider)
  useEffect(() => {
    imagesRef.current.forEach((img, i) => {
      if (img) {
        img.style.opacity = i <= activeIndex ? 1 : 0;
        img.style.zIndex = i + 2;
      }
    });
  }, [activeIndex, folderKey]);

  // Button group for toggling
  const handleButton = (key) => {
    if (key !== folderKey) setFolderKey(key);
  };

  return (
    <div style={{ width: '100%', maxWidth: 600, margin: '0 auto', height: '75vh', display: 'flex', flexDirection: 'column' }}>
      <div
        ref={containerRef}
        className='shadow-sm benefits-slider-container'
        style={{ position: 'relative', width: '100%', height: 'calc(100vh - 120px)', flex: 1 }}>
        {/* VAR and IDX overlays */}
        <div style={{ position: 'absolute', bottom: 10, left: 12, zIndex: 100, display: 'flex', flexDirection: 'column', gap: 0 }}>
          <span
            style={{
              fontFamily: 'var(--font-b)',
              fontSize: '11px',
              color: 'var(--c0)',
              letterSpacing: '0.08em',
              fontWeight: 500,
              lineHeight: 1.1,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            var_{folderKey.toLowerCase()}
          </span>
          <span
            style={{
              fontFamily: 'var(--font-b)',
              fontSize: '11px',
              color: 'var(--c0)',
              letterSpacing: '0.08em',
              fontWeight: 500,
              lineHeight: 1.1,
              userSelect: 'none',
              pointerEvents: 'none',
            }}
          >
            idx_{codes[activeIndex]}
          </span>
        </div>
        {/* Overlayed toggle buttons at bottom center */}
        <div style={{
          position: 'absolute',
          left: '50%',
          bottom: 24,
          transform: 'translateX(-50%)',
          zIndex: 110,
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
          borderRadius: '999px',
          padding: '4px 16px',
        }}>
          <button
            type="button"
            onClick={() => handleButton('AGE')}
            style={{
              fontFamily: 'var(--font-a)',
              fontSize: 13,
              fontWeight: 600,
              padding: '6px 18px',
              borderRadius: 'var(--border-radius)',
              border: folderKey === 'AGE' ? 'none' : '1px solid var(--c0)',
              background: folderKey === 'AGE' ? 'var(--ab-c0)' : 'var(--ab-c00)',
              color: 'var(--c0)',
              letterSpacing: '0.08em',
              cursor: folderKey === 'AGE' ? 'default' : 'pointer',
              boxShadow: folderKey === 'AGE' ? '0 2px 8px rgba(53,32,214,0.10)' : 'none',
              transition: 'all 0.18s',
              outline: 'none',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            }}
          >
            AGE
          </button>
          <button
            type="button"
            onClick={() => handleButton('ETH')}
            style={{
              fontFamily: 'var(--font-a)',
              fontSize: 13,
              fontWeight: 600,
              padding: '6px 18px',
              borderRadius: 'var(--border-radius)',
              border: folderKey === 'ETH' ? 'none' : '1px solid var(--c0)',
              background: folderKey === 'ETH' ? 'var(--ab-c0)' : 'var(--ab-c00)',
              color: 'var(--c0)',
              letterSpacing: '0.08em',
              cursor: folderKey === 'ETH' ? 'default' : 'pointer',
              boxShadow: folderKey === 'ETH' ? '0 2px 8px rgba(53,32,214,0.10)' : 'none',
              transition: 'all 0.18s',
              outline: 'none',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            }}
          >
            ETHNICITY
          </button>
        </div>
        {IMAGES.map((src, i) => (
          <React.Fragment key={i}>
            <SimpleResponsivePicture
              ref={el => imagesRef.current[i] = el}
              src={src}
              alt={`Slide ${i + 1}`}
              widths={[600, 800, 1000]}
              quality={85}
              sizes="(max-width: 768px) 100vw, 600px"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              imgStyle={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top',
                opacity: i <= activeIndex ? 1 : 0,
                zIndex: i + 2,
                transition: `opacity ${ANIMATION_DURATION}s`,
                borderRadius: BORDER_RADIUS
              }}
            />
            {/* SVG glyph in top right */}
            <svg
              width={20}
              height={20}
              viewBox="0 0 24 24"
              style={{
                position: 'absolute',
                top: 10,
                right: 12,
                zIndex: i + 2,
                display: 'block',
                pointerEvents: 'none',
                opacity: i <= activeIndex ? 1 : 0,
                transition: `opacity ${ANIMATION_DURATION}s`,
              }}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d={glyphPaths[`g0${i + 1}`]?.match(/d="([^"]+)"/)?.[1] || ''} fill="var(--c0)" />
            </svg>
          </React.Fragment>
        ))}
      </div>
      {/* Slider below the image */}
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 24 }}>
        <input
          type="range"
          min={0}
          max={IMAGE_COUNT - 1}
          value={activeIndex}
          onChange={handleSliderChange}
          className="benefits-slider"
          style={{
            width: '40%',
            background: 'transparent',
            '--percent': `${(activeIndex / (IMAGE_COUNT - 1)) * 100}`,
            '--slider-color': 'var(--c00)',
          }}
        />
      </div>
    </div>
  );
};

export default BenefitsImageSlider; 